import React, { Suspense, lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import Layout from "../components/Layout";
import ProtectedRoute from "../components/ProtectedRoute";
import SuperAdminRoute from "../components/SuperAdminRoute";

// Login and Dashboard are eager: Login is the entry point and Dashboard is the
// landing page, so code-splitting them would only add a round-trip.
import Login from "../pages/Login";
import Dashboard from "../pages/Dashboard";

// Every other page is split into its own chunk. This keeps the initial download
// small — in particular the charting library (recharts), which is only used by
// Consumption and Billing stats, no longer ships to admins who never open them.
const Users = lazy(() => import("../pages/Users"));
const UserDetail = lazy(() => import("../pages/UserDetail"));
const SimCards = lazy(() => import("../pages/SimCards"));
const ImsiMaps = lazy(() => import("../pages/ImsiMaps"));
const OfferNumbers = lazy(() => import("../pages/OfferNumbers"));
const Numbers = lazy(() => import("../pages/Numbers"));
const EsimOrders = lazy(() => import("../pages/EsimOrders"));
const ConsumptionPage = lazy(() => import("../pages/Consumption"));
const PushNotifications = lazy(() => import("../pages/PushNotifications"));
const BillingStats = lazy(() => import("../pages/BillingStats"));
const AdminUsers = lazy(() => import("../pages/AdminUsers"));
const WhiteLabel = lazy(() => import("../pages/WhiteLabel"));
const Brands = lazy(() => import("../pages/Brands"));
const PartnerSms = lazy(() => import("../pages/PartnerSms"));

/** Shown while a route chunk is being fetched. */
const PageLoading: React.FC = () => (
  <div className="empty" style={{ padding: 40 }}>
    <p>Loading…</p>
  </div>
);

/** Wraps a lazily-loaded page in the shared loading fallback. */
const page = (Component: React.ComponentType) => (
  <Suspense fallback={<PageLoading />}>
    <Component />
  </Suspense>
);

export const router = createBrowserRouter([
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/",
        element: <Layout />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: "dashboard",          element: <Dashboard /> },
          { path: "users",              element: page(Users) },
          { path: "users/:id",          element: page(UserDetail) },
          { path: "sim-cards",          element: page(SimCards) },
          { path: "imsi-maps",          element: page(ImsiMaps) },
          { path: "numbers",            element: page(Numbers) },
          { path: "esim-orders",        element: page(EsimOrders) },
          { path: "consumption",        element: page(ConsumptionPage) },
          { path: "push-notifications", element: page(PushNotifications) },
          // Billing stats is available to WL admins too (scoped to their brand),
          // so it stays outside the superadmin guard — mirrors Sidebar gating.
          { path: "billing/stats",      element: page(BillingStats) },
          { path: "brands",            element: page(Brands) },
          { path: "partner-sms",       element: page(PartnerSms) },
          // Superadmin-only routes (mirror Sidebar's superAdminOnly items).
          {
            element: <SuperAdminRoute />,
            children: [
              { path: "offer-numbers",  element: page(OfferNumbers) },
              { path: "admin-users",    element: page(AdminUsers) },
              { path: "white-label",    element: page(WhiteLabel) },
            ],
          },
        ],
      },
    ],
  },
]);
